import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, SegmentedControl, Skeleton, TextField } from "../components/ui";
import { fetchPantryItems } from "../lib/api";
import {
  AddShoppingListItemInput,
  ShoppingListItem,
  ShoppingListState,
  addItem,
  clearAll,
  clearChecked,
  formatAsText,
  groupByCategory,
  loadShoppingList,
  normalizeCategoryLabel,
  removeItem,
  saveShoppingList,
  summarize,
  toggleItem,
  updateItem,
} from "../lib/shoppingList";
import { trackEvent } from "../lib/analytics";
import { assetUrl } from "../lib/assets";
import type { PantryItem } from "../types";

type FilterMode = "active" | "completed" | "all";

const FILTER_OPTIONS: { value: FilterMode; label: string }[] = [
  { value: "active", label: "To buy" },
  { value: "completed", label: "Bought" },
  { value: "all", label: "All" },
];

const QUICK_CATEGORIES = [
  "Produce",
  "Dairy",
  "Pantry",
  "Frozen",
  "Bakery",
  "Beverages",
  "Snacks",
  "Household",
];

function formatRelative(iso?: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

interface AddRowProps {
  onAdd: (input: AddShoppingListItemInput) => void;
  suggestedCategories: string[];
}

function AddRow({ onAdd, suggestedCategories }: AddRowProps) {
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [category, setCategory] = useState("");

  const allSuggestions = useMemo(() => {
    const set = new Set<string>(QUICK_CATEGORIES);
    for (const value of suggestedCategories) {
      if (value) set.add(normalizeCategoryLabel(value));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [suggestedCategories]);

  function submit() {
    if (!name.trim()) return;
    onAdd({
      name,
      quantity: quantity || null,
      category: category || null,
      source: "manual",
    });
    setName("");
    setQuantity("");
    setCategory("");
  }

  return (
    <Card>
      <h3 className="text-base font-semibold">Add an item</h3>
      <p className="text-sm text-[var(--pr-text-muted)]">Drop in something to pick up on your next run.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-[1.4fr_0.8fr_1fr_auto]">
        <TextField label="Item" value={name} onChange={setName} placeholder="e.g. Oat milk" />
        <TextField label="Quantity" value={quantity} onChange={setQuantity} placeholder="2 cartons" />
        <label className="flex flex-col gap-2 text-sm">
          <span className="pr-label">Category</span>
          <input
            list="shopping-list-categories"
            value={category}
            placeholder="Choose or type"
            onChange={(event) => setCategory(event.target.value)}
            className="w-full rounded-lg border border-[var(--pr-border)] bg-[var(--pr-surface-container)] px-3 py-2 text-sm text-[var(--pr-text)] outline-none ring-[var(--pr-accent)] transition focus:ring-1"
          />
          <datalist id="shopping-list-categories">
            {allSuggestions.map((value) => (
              <option key={value} value={value} />
            ))}
          </datalist>
        </label>
        <div className="flex items-end">
          <Button onClick={submit} disabled={!name.trim()} className="w-full sm:w-auto">
            Add
          </Button>
        </div>
      </div>
    </Card>
  );
}

interface PantryQuickAddProps {
  onAdd: (input: AddShoppingListItemInput) => void;
  alreadyAddedNames: Set<string>;
}

function PantryQuickAdd({ onAdd, alreadyAddedNames }: PantryQuickAddProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<PantryItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchPantryItems(100);
      setItems(response.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load pantry items.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && items.length === 0 && !error) {
      void load();
    }
  }, [open, items.length, error, load]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const inList = alreadyAddedNames.has(item.item_name.toLowerCase());
      if (inList) return false;
      if (!query) return true;
      return (
        item.item_name.toLowerCase().includes(query) ||
        (item.category || "").toLowerCase().includes(query)
      );
    });
  }, [items, search, alreadyAddedNames]);

  function handleSelect(item: PantryItem) {
    onAdd({
      name: item.item_name,
      category: item.category,
      source: "pantry",
    });
    trackEvent("shopping_list_pantry_add", { category: normalizeCategoryLabel(item.category) });
  }

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">From your pantry</h3>
          <p className="text-sm text-[var(--pr-text-muted)]">Restock items already on your shelves with one tap.</p>
        </div>
        <Button
          variant={open ? "ghost" : "secondary"}
          className="min-h-10 px-3 text-sm"
          onClick={() => {
            const next = !open;
            setOpen(next);
            trackEvent("shopping_list_pantry_panel_toggled", { open: next });
          }}
        >
          {open ? "Hide pantry" : "Browse pantry"}
        </Button>
      </div>

      {open ? (
        <div className="mt-3 space-y-3">
          <TextField label="Search pantry" value={search} onChange={setSearch} placeholder="Search item or category" />
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
            </div>
          ) : error ? (
            <ErrorState message={error} onRetry={() => void load()} />
          ) : filtered.length === 0 ? (
            <p className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-container-low)] px-3 py-4 text-sm text-[var(--pr-text-muted)]">
              {items.length === 0
                ? "Pantry is empty. Scan your pantry to populate it, then come back to restock."
                : "Everything matching is already on your list."}
            </p>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {filtered.slice(0, 30).map((item) => (
                <li key={item.pantry_item_id ?? `${item.submission_id}-${item.item_name}`}>
                  <button
                    type="button"
                    onClick={() => handleSelect(item)}
                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 text-left text-sm transition hover:border-[var(--pr-accent)]"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold">{item.item_name}</span>
                      <span className="block truncate text-xs text-[var(--pr-text-muted)]">
                        {normalizeCategoryLabel(item.category)}
                      </span>
                    </span>
                    <span aria-hidden className="text-base text-[var(--pr-accent)]">＋</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </Card>
  );
}

interface ItemRowProps {
  item: ShoppingListItem;
  onToggle: () => void;
  onRemove: () => void;
  onRename: () => void;
}

function ItemRow({ item, onToggle, onRemove, onRename }: ItemRowProps) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2.5 text-sm">
      <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={item.checked}
          onChange={onToggle}
          className="mt-1 h-4 w-4 cursor-pointer accent-[var(--pr-accent)]"
        />
        <span className="min-w-0">
          <span
            className={`block truncate font-semibold ${
              item.checked ? "text-[var(--pr-text-muted)] line-through" : "text-[var(--pr-text)]"
            }`}
          >
            {item.name}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--pr-text-muted)]">
            {item.quantity ? <span>{item.quantity}</span> : null}
            {item.source === "pantry" ? <Pill tone="accent" label="From pantry" /> : null}
            {item.notes ? <span className="italic">{item.notes}</span> : null}
          </span>
        </span>
      </label>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onRename}
          className="rounded-md px-2 py-1 text-xs font-semibold text-[var(--pr-text-muted)] hover:text-[var(--pr-text)]"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${item.name}`}
          className="rounded-md px-2 py-1 text-xs font-semibold text-rose-500 hover:text-rose-400"
        >
          Remove
        </button>
      </div>
    </li>
  );
}

export function ShoppingListPage() {
  const [state, setState] = useState<ShoppingListState>(() => loadShoppingList());
  const [filter, setFilter] = useState<FilterMode>("active");
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");

  useEffect(() => {
    saveShoppingList(state);
  }, [state]);

  const summary = useMemo(() => summarize(state), [state]);

  const activeItems = useMemo(() => state.items.filter((item) => !item.checked), [state.items]);
  const completedItems = useMemo(() => state.items.filter((item) => item.checked), [state.items]);

  const visibleGroups = useMemo(() => {
    const visible =
      filter === "active" ? activeItems : filter === "completed" ? completedItems : state.items;
    return groupByCategory(visible);
  }, [activeItems, completedItems, filter, state.items]);

  const activeNamesLower = useMemo(() => {
    const set = new Set<string>();
    for (const item of activeItems) {
      set.add(item.name.toLowerCase());
    }
    return set;
  }, [activeItems]);

  const handleAdd = useCallback((input: AddShoppingListItemInput) => {
    setState((current) => addItem(current, input));
    trackEvent("shopping_list_item_added", {
      source: input.source ?? "manual",
      has_quantity: Boolean((input.quantity ?? "").trim()),
      category: normalizeCategoryLabel(input.category),
    });
  }, []);

  function handleToggle(id: string) {
    setState((current) => {
      const next = toggleItem(current, id);
      const target = next.items.find((item) => item.id === id);
      if (target) {
        trackEvent("shopping_list_item_toggled", {
          checked: target.checked,
          source: target.source,
        });
      }
      return next;
    });
  }

  function handleRemove(id: string) {
    setState((current) => {
      const target = current.items.find((item) => item.id === id);
      if (target) {
        trackEvent("shopping_list_item_removed", { source: target.source });
      }
      return removeItem(current, id);
    });
  }

  function handleRename(id: string) {
    const target = state.items.find((item) => item.id === id);
    if (!target) return;
    const nextName = window.prompt("Rename item", target.name)?.trim();
    if (!nextName || nextName === target.name) return;
    setState((current) => updateItem(current, id, { name: nextName }));
    trackEvent("shopping_list_item_renamed", { source: target.source });
  }

  function handleClearCompleted() {
    if (completedItems.length === 0) return;
    setState((current) => clearChecked(current));
    trackEvent("shopping_list_cleared_completed", { removed_count: completedItems.length });
  }

  function handleClearAll() {
    if (state.items.length === 0) return;
    const confirmed = window.confirm(`Clear all ${state.items.length} items from your shopping list?`);
    if (!confirmed) return;
    setState((current) => clearAll(current));
    trackEvent("shopping_list_cleared_all", { removed_count: state.items.length });
  }

  async function handleCopy() {
    const text = formatAsText(state);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setCopyStatus("copied");
        trackEvent("shopping_list_copied", { item_count: summary.remaining });
      } else {
        throw new Error("Clipboard unavailable");
      }
    } catch {
      setCopyStatus("error");
    }
    window.setTimeout(() => setCopyStatus("idle"), 2500);
  }

  return (
    <div data-testid="shopping-list-page" className="space-y-4 pb-4">
      <PageHeader
        title="Shopping list"
        subtitle="Plan what to pick up next. Pull from your pantry or add new items."
        right={
          <div className="flex items-center gap-2">
            <Button variant="secondary" className="min-h-10 px-3 text-sm" onClick={handleCopy} disabled={summary.remaining === 0}>
              {copyStatus === "copied" ? "Copied!" : copyStatus === "error" ? "Copy failed" : "Copy"}
            </Button>
          </div>
        }
      />

      <Card flat className="grid gap-3 sm:grid-cols-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Total items</p>
          <p className="pr-display mt-1 text-2xl font-semibold">{summary.total}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">To buy</p>
          <p className="pr-display mt-1 text-2xl font-semibold">{summary.remaining}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Bought</p>
          <p className="pr-display mt-1 text-2xl font-semibold">{summary.completed}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Categories</p>
          <p className="pr-display mt-1 text-2xl font-semibold">{summary.categories.length}</p>
        </div>
      </Card>

      <AddRow onAdd={handleAdd} suggestedCategories={summary.categories} />

      <PantryQuickAdd onAdd={handleAdd} alreadyAddedNames={activeNamesLower} />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SegmentedControl<FilterMode>
            value={filter}
            options={FILTER_OPTIONS}
            onChange={(value) => {
              setFilter(value);
              trackEvent("shopping_list_filter_changed", { filter: value });
            }}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              className="min-h-10 border border-[var(--pr-border)] px-3 text-sm"
              onClick={handleClearCompleted}
              disabled={summary.completed === 0}
            >
              Clear bought ({summary.completed})
            </Button>
            <Button
              variant="ghost"
              className="min-h-10 border border-[var(--pr-border)] px-3 text-sm text-rose-500"
              onClick={handleClearAll}
              disabled={summary.total === 0}
            >
              Clear all
            </Button>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {visibleGroups.length === 0 ? (
            filter === "completed" ? (
              <p className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-container-low)] px-3 py-4 text-sm text-[var(--pr-text-muted)]">
                Nothing bought yet. Check items off as you grab them.
              </p>
            ) : (
              <EmptyState
                title="Your shopping list is empty"
                subtitle="Add items above or pull from your pantry to start planning your next run."
                image={assetUrl("pantry-inventory-bg.png")}
                action={
                  <Link
                    to="/app/pantry"
                    className="pr-btn-secondary inline-flex min-h-10 items-center px-4 text-sm font-semibold"
                  >
                    Open pantry
                  </Link>
                }
              />
            )
          ) : (
            visibleGroups.map((group) => (
              <div key={group.category}>
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">
                    {group.category}
                  </h4>
                  <span className="text-xs text-[var(--pr-text-muted)]">
                    {group.items.filter((item) => !item.checked).length} to buy
                  </span>
                </div>
                <ul className="space-y-2">
                  {group.items.map((item) => (
                    <ItemRow
                      key={item.id}
                      item={item}
                      onToggle={() => handleToggle(item.id)}
                      onRemove={() => handleRemove(item.id)}
                      onRename={() => handleRename(item.id)}
                    />
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </Card>

      {state.updatedAt && state.items.length > 0 ? (
        <p className="text-center text-xs text-[var(--pr-text-muted)]">
          Saved locally on this device · last updated {formatRelative(state.updatedAt)}
        </p>
      ) : null}
    </div>
  );
}
